import { LocalizationService } from '@abp/ng.core';
import { Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const INPUT_SELECTOR = 'input';
const PASSWORD_INPUT_SELECTOR = 'input[type="password"]';
const MANAGED_MARKER_ATTRIBUTE = 'ca-password-placeholder';
const IGNORE_ATTRIBUTE = 'ignore-ca-password-placeholder';
const FORMCONTROLLER_ATTRIBUTE = 'formcontrolname';
const FORMCONTROLLER_VALUE = 'clientSecret';
const PLACEHOLDER_ATTRIBUTE = 'placeholder';
const PLACEHOLDER_LOCALIZATION_KEY = 'Settings::MaskedAreaPlaceholder';
const MAX_PENDING_RECORDS = 200;

/**
 * Applies the masked-area placeholder to every password input in the document, including
 * inputs rendered by ABP's own NgModules
 * (Angular resolves directives against the declaring module's compilation scope).
 */
@Injectable({ providedIn: 'root' })
export class PasswordPlaceholderService {
  private observer?: MutationObserver;
  private scheduled = false;
  private pendingRecords: MutationRecord[] = [];
  private needsFullPass = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private localizationService: LocalizationService,
    private ngZone: NgZone,
  ) {}

  /** Starts observing the document. Safe to call more than once. */
  start(): void {
    if (this.observer) {
      return;
    }

    this.localizationService.languageChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshManagedPlaceholders());

    this.ngZone.runOutsideAngular(() => {
      this.applyToDocument();

      this.observer = new MutationObserver(records => {
        // requestAnimationFrame never fires in a backgrounded tab, so the buffer would grow without
        // bound and retain detached nodes; a full pass covers strictly more than the dropped records.
        if (this.pendingRecords.length + records.length > MAX_PENDING_RECORDS) {
          this.pendingRecords = [];
          this.needsFullPass = true;
        } else {
          this.pendingRecords.push(...records);
        }

        if (this.scheduled) {
          return;
        }

        this.scheduled = true;
        requestAnimationFrame(() => {
          this.scheduled = false;
          const batch = this.pendingRecords;
          this.pendingRecords = [];

          if (this.needsFullPass) {
            this.needsFullPass = false;
            this.applyToDocument();
            return;
          }

          this.processRecords(batch);
        });
      });

      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['type'],
      });
    });
  }

  /** Stops observing and releases the language-change subscription. */
  stop(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    this.scheduled = false;
    this.pendingRecords = [];
    this.needsFullPass = false;
    // Not completed: the subject has to stay usable so a later start() can resubscribe.
    this.destroy$.next();
  }

  private processRecords(records: MutationRecord[]): void {
    records.forEach(record => {
      if (record.type === 'childList') {
        record.addedNodes.forEach(node => this.applyToAddedNode(node));
        return;
      }

      if (record.type === 'attributes') {
        this.applyToElement(record.target as Element);
      }
    });
  }

  private applyToAddedNode(node: Node): void {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = node as Element;
    this.applyToElement(element);
    element.querySelectorAll(INPUT_SELECTOR).forEach(descendant => this.applyToElement(descendant));
  }

  private applyToElement(element: Element): void {
    if (
      !element.matches(PASSWORD_INPUT_SELECTOR) &&
      (!element.hasAttribute(FORMCONTROLLER_ATTRIBUTE) ||
        element.getAttribute(FORMCONTROLLER_ATTRIBUTE) !== FORMCONTROLLER_VALUE)
    ) {
      return;
    }

    if (element.hasAttribute(MANAGED_MARKER_ATTRIBUTE)) {
      return;
    }

    // A placeholder already set by our own templates (sometimes conditionally bound) wins.
    if (element.hasAttribute(PLACEHOLDER_ATTRIBUTE)) {
      return;
    }

    if (element.hasAttribute(IGNORE_ATTRIBUTE)) {
      return;
    }

    element.setAttribute(
      PLACEHOLDER_ATTRIBUTE,
      this.localizationService.instant(PLACEHOLDER_LOCALIZATION_KEY),
    );
    element.setAttribute(MANAGED_MARKER_ATTRIBUTE, 'true');
  }

  private applyToDocument(): void {
    document.querySelectorAll(INPUT_SELECTOR).forEach(element => this.applyToElement(element));
  }

  private refreshManagedPlaceholders(): void {
    document.querySelectorAll(`[${MANAGED_MARKER_ATTRIBUTE}]`).forEach(element => {
      element.removeAttribute(PLACEHOLDER_ATTRIBUTE);
      element.removeAttribute(MANAGED_MARKER_ATTRIBUTE);
    });

    this.applyToDocument();
  }
}
